import { ChevronRight } from 'lucide-react';

import { cn } from '@rocco/utils/cn';
import ProductAttributeEnum from '@modules/product/config/product-attribute';
import { useVariantOptionState } from '@modules/product/context/VariantOptionContext';
import type Variant from '@modules/product/type/variant';
import type ProductSize from '@modules/product/type/product-size';

export interface MobileVariantRowsProps {
    variants: Variant[];
    /** Sizes actually used by a variant (the legacy `sizesInUse` filter). */
    sizes: ProductSize[];
    /** A summary row was tapped — the parent opens the purchase drawer. */
    onSpecify: () => void;
}

interface SummaryRow {
    key: string;
    label: string;
    /** Current pick, or the placeholder when nothing is chosen yet. */
    value: string;
    /** True while the row shows the "Select" placeholder (renders grey). */
    placeholder: boolean;
    /** Colour rows preview the picked swatch beside its name. */
    swatch?: { image?: string | null; code?: string | null };
}

/**
 * The collapsed mobile variant picker: one summary row per option group
 * ("Colour — Black ›"), each opening the purchase drawer, where the pick is
 * made and the transaction completed. Requires the variant option/selection
 * providers above it. Renders nothing when there is nothing to configure.
 */
export const MobileVariantRows = ({
    variants,
    sizes,
    onSpecify,
}: MobileVariantRowsProps) => {
    const { attributeMap, decision } = useVariantOptionState();

    const attributes = [...attributeMap.values()];
    if (attributes.length === 0 && sizes.length === 0) return null;

    // Mirrors MobileVariantSelector: a product whose variants all carry a size
    // has no "Not Applicable" state — nothing picked yet just means "Select".
    const sizeIsGeneric =
        variants.length === 0 || variants.every((v) => Boolean(v.Size));

    const rows: SummaryRow[] = attributes.map((attribute) => {
        const selectedId = decision.options[attribute.ID] ?? 0;
        const option = attribute.options.find((o) => o.ID === selectedId);
        const placeholder = !option && attribute.isGeneric;
        return {
            key: `attribute-${attribute.ID}`,
            label: attribute.Name,
            value: option?.Label ?? (placeholder ? 'Select' : 'Not Applicable'),
            placeholder,
            // Only when there is something to show: the label beside it
            // already names the colour, and a colourway with neither a
            // texture nor a hex drew as a blank chip.
            swatch:
                attribute.ID === ProductAttributeEnum.Colour &&
                option &&
                (option.Image?.Desktop || option.Code)
                    ? {
                          image: option.Image?.Desktop,
                          code: option.Code,
                      }
                    : undefined,
        };
    });
    if (sizes.length > 0) {
        const selectedSize = sizes.find((size) => size.ID === decision.size);
        const placeholder = !selectedSize && sizeIsGeneric;
        rows.push({
            key: 'size',
            label: 'Size',
            value:
                selectedSize?.FullTitle ??
                (placeholder ? 'Select' : 'Not Applicable'),
            placeholder,
        });
    }

    return (
        <div className="[&:empty]:hidden">
            {rows.map((row) => (
                <button
                    key={row.key}
                    type="button"
                    onClick={onSpecify}
                    className="flex w-full items-center justify-between gap-4 border-b border-black/10 py-3.5 text-left first:border-t"
                >
                    <span className="shrink-0 font-sans text-sm text-[#7f7f7f]">
                        {row.label}
                    </span>
                    <span
                        className={cn(
                            'flex min-w-0 items-center gap-2 font-sans text-sm',
                            row.placeholder
                                ? 'text-[#767676]'
                                : 'text-[#181818]',
                        )}
                    >
                        {row.swatch ? (
                            <span
                                aria-hidden="true"
                                className="size-4 shrink-0 rounded-full border border-black/10 bg-cover bg-center"
                                style={
                                    row.swatch.image
                                        ? {
                                              backgroundImage: `url(${row.swatch.image})`,
                                          }
                                        : {
                                              backgroundColor:
                                                  row.swatch.code ?? '#f1f1f1',
                                          }
                                }
                            />
                        ) : null}
                        <span className="truncate">{row.value}</span>
                        <ChevronRight className="size-4 shrink-0 text-[#767676]" />
                    </span>
                </button>
            ))}
        </div>
    );
};
